import { useQueryClient } from '@tanstack/react-query';

import { useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToaster } from '../../../../hooks/useToaster/useToaster.js';
import { featuresSelectors } from '../../../../redux/features/selectors/featuresSelectors.js';
import { useAppSelector } from '../../../../redux/hooks.js';
import { OnboardingModuleProps } from '../../types.js';
import { OnboardingFormStep } from '../OnboardingFormStep/OnboardingFormStep.js';
import { SubscriptionStep } from '../SubscriptionStep/SubscriptionStep.js';

const { selectShouldAllowTrialWithoutPayment } = featuresSelectors;

type Props = {
  referralCode: OnboardingModuleProps['referralCode'];
  mcKissockOffer: OnboardingModuleProps['mcKissockOffer'];
  SubscriptionModule: OnboardingModuleProps['SubscriptionModule'];
};

export function OnboardingWizard({ referralCode, mcKissockOffer, SubscriptionModule }: Props) {
  const [step, incrementStep] = useReducer((prev: number) => prev + 1, 0);
  const toast = useToaster();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const allowTrialWithoutPayment = useAppSelector((state) =>
    selectShouldAllowTrialWithoutPayment(state, null),
  );

  if (allowTrialWithoutPayment) {
    return (
      <OnboardingFormStep
        referralCode={referralCode}
        mcKissockOffer={mcKissockOffer}
        onFinish={async () => {
          await queryClient.invalidateQueries({
            queryKey: ['api', 'v2', 'stripe', 'subscriptionStatus'],
          });
          navigate('/orders');
        }}
      />
    );
  }

  if (step === 0) {
    return (
      <OnboardingFormStep
        referralCode={referralCode}
        mcKissockOffer={mcKissockOffer}
        onFinish={incrementStep}
      />
    );
  } else if (step === 1) {
    return (
      <SubscriptionStep
        SubscriptionModule={SubscriptionModule}
        onFinish={async () => {
          toast({
            id: 'onboarding-subscription-success',
            type: 'success',
            title: 'Subscription successful!',
            body: 'Thank you for subscribing to Appraiser Toolkit!',
          });

          await queryClient.invalidateQueries({
            queryKey: ['api', 'v2', 'stripe', 'subscriptionStatus'],
          });

          navigate('/orders');
        }}
      />
    );
  } else {
    throw new Error(`Invalid step: ${step}`);
  }
}
